
import Vue from 'vue';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { Component, Emit, Inject, Prop } from 'vue-property-decorator';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import Toasted from 'vue-toasted';
import Datepicker from 'vuejs-datepicker';
import { de } from 'vuejs-datepicker/dist/locale';
import { blobDownload, formatDate, WindowType } from '../../../util';
import { PqtApiUiV2Client, PqtConfig, PqtProduct, PqtProductTest } from '../../backend/backendapi';
import { FileStatusMessage, StatusMessage } from '../../dropzone/dropzone.util';
import { RiskOption } from '../archivemodal/archivemodal.model';
import PqtProductTestEditModal from '../test/PqtProductTestEditModal';
import { paginationOptions, searchOptions } from '../../../util/vue-good-table';
import State from '../State';
import PqtMeasurementSpecifications from './PqtMeasurementSpecifications';
import DropZone from '../../dropzone/DropZone';
import { Like } from '../../../util/Like';
import { handleError, initClient } from '../../../api';
import configHidden from '../../../directives/configHidden';
import configRequired from '../../../directives/configRequired';

declare var window: WindowType;

Vue.use(Toasted, { duration: 2000 });

@Component({
  components: {
    Datepicker,
    DropZone,
    PqtMeasurementSpecifications,
    PqtProductTestEditModal,
    vSelect,
    VueGoodTable,
    ImageViewer: require('../../imageviewer/imageviewer.vue.html').default,
  },
  directives: {
    configHidden,
    configRequired,
  },
})
export default class PqtEdit extends Vue {
  @Prop({ required: true })
  readonly product!: PqtProduct;
  @Prop({ required: true })
  readonly tests!: PqtProductTest[] | null;
  @Prop({ default: true })
  readonly editable!: boolean;
  @Prop({ default: true })
  readonly showArchiveFields!: boolean;

  @Inject({ from: 'state' })
  readonly state!: State;

  private readonly api = initClient(PqtApiUiV2Client);

  //config
  datepickerDE: any = de;
  selectConfig = {};

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get productEnabled(): boolean {
    return !this.product.disabled;
  }

  set productEnabled(value: boolean) {
    this.product.disabled = !value;
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  get uid(): string {
    return this._uid;
  }

  @Emit('save')
  save(): void {

  }

  async exportTest(test: Like<PqtProductTest>): Promise<void> {
    try {
      const file = await this.api.exportTestToExcel(test.productId, test.id);
      blobDownload(file.data, file.fileName ?? 'report.xlsx');
    } catch (err) {
      handleError(this.$toasted, this.$i18n, err);
    }
  }

  fileUploaded(status: FileStatusMessage): void {
    if (status.type !== StatusMessage.success) {
      return;
    }
    this.$emit('file-added', { fileId: status.fileId, product: this.product });
  }

  makeConfig(key: string): { config: PqtConfig, key: string } {
    return { config: this.state.config, key };
  }

  removeMedia(mediaId: string): void {
    console.log('rem', mediaId);
    let index = this.product.files.findIndex(e => e.id === mediaId);
    if (index > -1) {
      this.product.files.splice(index, 1);
    }
  }

  genCSV(): string {
    var table = this.$refs.table as VueGoodTable;

    var cols = this.testColumns.filter(e => e.field !== 'actions');
    var csv = '\ufeff' //utf8 bom (byte order marker)
        + cols.map(e => e.label).join(';') + '\n'
        + ((table.paginated?.length ?? 0) === 0 ? '' : table.paginated[0].children.map(row => {
          return cols.map(col => {
            if (typeof col.field === 'string') {
              return ('' + (row[col.field] ?? '')).split(';').join(',');
            } else if (typeof col.field === 'function') {
              return ('' + (col.field(row) ?? '')).split(';').join(',');
            } else {
              return 'cannot extract value';
            }
          }).join(';');
        }).join('\n'));

    console.log(csv);
    return csv;
  }

  exportCSV(): void {
    const data = this.genCSV();
    const fileName = 'export_tests.csv';

    var a = document.createElement('a') as any;
    document.body.appendChild(a);
    a.style = 'display: none';
    var blob = new Blob([data], { type: 'octet/stream' });
    var url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  get testColumns(): any[] {
    return [
      {
        label: this.$t('productQualityTests.edit.protocolNumber').toString(),
        field: 'protocolNumber',
      },
      {
        label: this.$t('productQualityTests.edit.testDate').toString(),
        field: (row: PqtProductTest) => formatDate(row.testDate),
      },
      {
        label: this.$t('productQualityTests.edit.testPerson').toString(),
        field: 'testPerson',
      },
      {
        label: this.$t('productQualityTests.edit.testSpecification').toString(),
        field: 'testSpecification',
      },
      {
        label: this.$t('productQualityTests.edit.error').toString(),
        field: 'error',
      },
      {
        label: this.$t('productQualityTests.edit.grade').toString(),
        field: 'grade',
      },
      {
        label: this.$t('productQualityTests.edit.note').toString(),
        field: 'note',
      },
      {
        label: this.$t('general.actions').toString(),
        field: 'actions',
      },
    ];
  }

  get allowMediaDelete(): boolean {
    return window.application.permissions.productQualityTests.mediaDelete;
  }

  get archiveRisk(): string {
    let localRiskOptions = this.state.riskOptionsSetting[this.$i18n.locale] as RiskOption[];
    localRiskOptions ??= this.state.riskOptionsSetting['en'] as RiskOption[];
    if (!localRiskOptions) {
      return this.product.archiveRisk!;
    }
    return localRiskOptions.find(e => e.id === this.product.archiveRisk!)?.name ?? this.product.archiveRisk!;
  }

  onClick(arg: { row: PqtProductTest, pageIndex: number, selected: boolean, event: MouseEvent }): void {
    (this.$refs.testModal as PqtProductTestEditModal).open(this.product, PqtProductTest.fromJS(arg.row));
  }
}
